import { useEffect, useState } from 'react';
import ShuttleService from '@services/ShuttleService';
import TrackingService from '@services/TrackingService';
import { serverErrorHandler } from '@services/HttpErrorsService';
import MARITAL_STATUS_TYPE from '@lib/common/constants/maritalStatusTypes';
import ANNUITY_TYPES from '@lib/common/constants/annuityTypes';
import SSGA_EXPERIENCE_TYPES from '@lib/income-wise/constants/experienceTypes';
import { PLAN_PRODUCT_TYPE_CODE_MAPPING } from 'micruity-utils/lib/mapping';

function useShuttle(shuttleRootPath, planId) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [error, setError] = useState({});
  const [currentParams, setCurrentParams] = useState({ shuttleRootPath, planId });

  // Clear data immediately if parameters changed
  if (currentParams.shuttleRootPath !== shuttleRootPath || currentParams.planId !== planId) {
    setData({});
    setError({});
    setIsLoading(true);
    setCurrentParams({ shuttleRootPath, planId });
  }

  useEffect(() => {
    const getShuttle = async () => {
      try {
        const response = await ShuttleService.get(shuttleRootPath, planId);

        TrackingService.setDeviceCode(response.participantMemory?.deviceCode, response.participantMemory?.deviceCodeBy);

        response.steps = buildSteps(response.config.steps);
        setData(response);
        setError({});
      } catch (err) {
        serverErrorHandler(err, setError);
      } finally {
        setIsLoading(false);
      }
    };
    getShuttle();

    return () => ShuttleService.cancelCurrentCall();
  }, [shuttleRootPath, planId]);

  return {
    data,
    isLoading,
    error,
    setError,
  };
}

export function filterEntities(entities, configProps) {
  const { hidden = [], priority = [], disabled = [] } = configProps;

  const priorityItems = priority
    // Only include if entity exists for this key and not hidden.
    .filter(key => entities[key] && !hidden.includes(key))
    .map(key => ({
      key,
      ...entities[key],
      isDisabled: disabled.includes(key),
    }));

  const regularItems = Object.entries(entities)
    .filter(([key]) => !priority.includes(key) && !hidden.includes(key))
    .map(([key, value]) => ({
      key,
      ...value,
      isDisabled: disabled.includes(key),
    }));

  return [...priorityItems, ...regularItems];
}

export function formatOptions(entities) {
  return entities.map(({ key, code, name, isDisabled }) => ({
    key,
    value: code,
    label: name,
    isDisabled,
  }));
}

export function buildSteps(steps, election) {
  const condititionedSteps = steps.reduce((finalSteps, currentStep) => {
    const stepCopy = { ...currentStep };

    if (stepCopy.children) {
      stepCopy.children = buildSteps(stepCopy.children, election);
    }

    // TODO: add a null check for show_conditions to prevent crashes
    if (currentStep.show_conditions.length === 0) {
      finalSteps.push(stepCopy);
      return finalSteps;
    }

    for (let i = 0; i < currentStep.show_conditions.length; i++) {
      const stepConditionFunc = stepConditionsList[currentStep.show_conditions[i]];
      if (!stepConditionFunc) {
        // eslint-disable-next-line no-console
        console.log('STEP CONDITIONS DOES NOT EXIST', currentStep.show_conditions[i]);
        return finalSteps;
      }
      if (!stepConditionFunc(election)) {
        return finalSteps;
      }
    }

    finalSteps.push(stepCopy);
    return finalSteps;
  }, []);

  return condititionedSteps;
}

// TODO - should be pulling STEP_CONDITION_KEYS from opt/layer-utils/lib/shuttleShowCondition
const stepConditionsList = {
  IS_BENEFICIARIES_ALLOWED : (election) => {
    return election?.insurer?.beneficiaries?.max > 0;
  },
  IS_NOT_MARRIED: (election) => {
    return election?.data?.marital_status !== MARITAL_STATUS_TYPE.MARRIED;
  },
  IS_MARRIED: (election) => {
    return election?.data?.marital_status === MARITAL_STATUS_TYPE.MARRIED || election?.data?.marital_status === 'M';
  },
  IS_JOINT_AND_SURVIVOR: (election) => {
    return election?.quote?.type === ANNUITY_TYPES.JOINT_AND_SURVIVOR;
  },
  IS_MARRIED_OR_JOINT_AND_SURVIVOR: (election) => {
    return election?.data?.marital_status === MARITAL_STATUS_TYPE.MARRIED || election?.quote?.type === ANNUITY_TYPES.JOINT_AND_SURVIVOR;
  },
  IS_VISIBLE_FOR_SSGA_ELECTION_EXPERIENCE: (election) => {
    return election?.data?.experience_type === SSGA_EXPERIENCE_TYPES.ELECTION;
  },
  IS_VISIBLE_FOR_SSGA_MODELING_EXPERIENCE: (election) => {
    return election?.data?.experience_type === SSGA_EXPERIENCE_TYPES.MODEL;
  },
  IS_VISIBLE_FOR_SSGA_QLAC_ELECTION: (election) => {
    return !!election?.data?.selections?.qlac;
  },
  IS_VISIBLE_FOR_SSGA_SPIA_ELECTION: (election) => {
    return !!election?.data?.selections?.drawdown;
  },
  IS_VISIBLE_FOR_SSGA_CURRENT_ELECTIONS: (election) => {
    return election?.data?.elections?.qlac;
  },
  IS_VISIBLE_FOR_SSGA_NO_CURRENT_ELECTIONS: (election) => {
    return !election?.data?.elections?.qlac;
  },
  IS_VISIBLE_FOR_BENEFIT_TYPE_SINGLE : (data) => {
    return data?.selected_benefit_types?.includes('S');
  },
  IS_VISIBLE_FOR_BENEFIT_TYPE_JOINT : (data) => {
    return data?.selected_benefit_types?.includes('J');
  },
  IS_VISIBLE_FOR_BENEFIT_TYPE_CERTAIN : (data) => {
    return data?.selected_benefit_types?.includes('C');
  },
  // eslint-disable-next-line no-unused-vars
  IS_ELIGIBLE_FOR_ELECTION: (election) => {
    return false; // TODO - update to check current date
  },
  IS_VISIBLE_FOR_PRODUCT_TYPE_QLAC: (election) => {
    return election?.product?.product_type === PLAN_PRODUCT_TYPE_CODE_MAPPING.QLAC;
  },
  IS_VISIBLE_FOR_PRODUCT_TYPE_SPIA: (election) => {
    return election?.product?.product_type === PLAN_PRODUCT_TYPE_CODE_MAPPING.SPIA;
  },
  // eslint-disable-next-line no-unused-vars
  IS_HIDDEN_FOOTER: (election) => {
    return true; // Used for metlife education shuttle to hide footer
  },
  IS_VISIBLE_FOR_ELIGIBLE_SRLI_ELECTION: () => {
    return false;
  },
  HAS_MULTIPLE_PRODUCTS: (election) => {
    const products = election?.products || [];
    const uniqueProductTypes = [...new Set(products.map(p => p.product_type).filter(Boolean))];
    return uniqueProductTypes.length > 1;
  },
};

export default useShuttle;

// Copyright © 2024 Micruity Inc.
